import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KnowledgeRetrievalPanel, HistoricalContextPanel } from "@/components/allison/IntelligencePanels";
import { OrganizerGovernance } from "@/components/allison/OrganizerGovernance";

const STATUS_TONE = {
  assigned: "bg-slate-100 text-slate-600", in_progress: "bg-blue-100 text-blue-700",
  staff_interview: "bg-indigo-100 text-indigo-700", staff_data_entry: "bg-indigo-100 text-indigo-700",
  waiting_on_client: "bg-amber-100 text-amber-700", submitted: "bg-violet-100 text-violet-700",
  complete: "bg-emerald-100 text-emerald-700", reopened: "bg-orange-100 text-orange-700",
};

function QuestionRow({ q, value, onChange, onDna, busy }) {
  const id = `org-q-${q.key}`;
  const dna = q.answer_status === "does_not_apply";
  const isUnanswered = q.answer_status === "unanswered" || (!dna && value === null && value === undefined);
  const provenance = q.is_staff_entered ? `Staff · ${(q.entry_method || "").replace(/_/g, " ")}`
    : (q.answer !== null && q.answer !== undefined && q.answer !== "" ? "Client" : null);
  return (
    <div className="py-3 px-2 border-b border-slate-50 last:border-0" data-testid={`org-question-${q.key}`}>
      <div className="flex items-start justify-between gap-3">
        <label htmlFor={id} className="text-sm text-slate-700 flex-1">
          {q.label}{q.required && <span className="text-rose-500 ml-0.5">*</span>}
          {q.waived && <span className="ml-2 text-[11px] text-slate-400">(waived)</span>}
        </label>
        <div className="flex items-center gap-2 shrink-0">
          {provenance && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{provenance}</span>}
          {/* Staff display: DNA badge vs Not Provided indicator */}
          {dna && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium" data-testid={`org-q-${q.key}-dna-badge`}>
              Does Not Apply
            </span>
          )}
          {!dna && !provenance && q.required && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`org-q-${q.key}-not-provided`}>
              Not Provided
            </span>
          )}
          {q.allow_dna !== false && (
            <button type="button" onClick={() => onDna(!dna)} disabled={busy} data-testid={`org-q-${q.key}-dna`}
              className={`text-[10px] font-medium rounded-full px-2 py-0.5 border transition-colors ${dna ? "bg-slate-700 text-white border-slate-700" : "border-slate-200 text-slate-500 hover:border-slate-400"} disabled:opacity-50`}>
              {dna ? "✓ N/A" : "N/A"}
            </button>
          )}
        </div>
      </div>
      {dna ? (
        <p className="mt-2 px-2 text-xs text-slate-400 italic">Marked Does Not Apply.</p>
      ) : (
      <div className="mt-2">
        {q.type === "boolean" || q.type === "yes_no" ? (
          <div className="flex gap-2">
            {[["Yes", true], ["No", false]].map(([lbl, v]) => (
              <button key={lbl} type="button" data-testid={`org-q-${q.key}-${lbl.toLowerCase()}`}
                onClick={() => onChange(v)}
                className={`text-sm px-4 py-1.5 rounded-lg border transition-colors ${value === v ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {lbl}
              </button>
            ))}
          </div>
        ) : q.type === "select" ? (
          <select id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            className="w-full sm:w-72 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Select…</option>
            {(q.options || []).map((o) => { const ov = typeof o === "object" ? o.value : o; const ol = typeof o === "object" ? o.label : String(o).replace(/_/g, " "); return <option key={ov} value={ov}>{ol}</option>; })}
          </select>
        ) : q.type === "number" || q.type === "currency" ? (
          <input id={id} data-testid={id} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            className="w-full sm:w-72 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        ) : (
          <input id={id} data-testid={id} type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            className="w-full sm:w-72 border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        )}
      </div>
      )}
    </div>
  );
}

export default function StaffOrganizer() {
  const { oid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("interview");
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(null); // {action, question_key, section_key}

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/organizers/${oid}`); setData(data); setDraft({}); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [oid]);
  useEffect(() => { load(); }, [load]);

  const org = data?.organizer;
  const state = data?.state;
  const client = data?.client;
  const dirty = Object.keys(draft).length > 0;
  const getVal = (q) => (q.key in draft ? draft[q.key] : q.answer);

  // DRL-2A: AI-prepared answers (proposal stage — not yet canonical)
  const aiProposals = org?.ai_prepared_answers || {};
  const aiMeta = org?.ai_preparation_meta;
  const aiProposalCount = Object.keys(aiProposals).filter(k => !aiProposals[k]?.dismissed).length;

  const acceptAiAnswer = async (questionKey, overrideValue) => {
    setBusy(`ai-accept-${questionKey}`);
    try {
      const body = { question_key: questionKey };
      if (overrideValue !== undefined) body.value = overrideValue;
      await api.post(`/organizers/${oid}/ai-answer/accept`, body);
      toast.success("Answer accepted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const dismissAiAnswer = async (questionKey) => {
    setBusy(`ai-dismiss-${questionKey}`);
    try {
      await api.post(`/organizers/${oid}/ai-answer/dismiss`, { question_key: questionKey });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveAnswers = async () => {
    if (!dirty) return;
    setBusy("save");
    try {
      await api.post(`/organizers/${oid}/answers`, {
        answers: draft, entry_method: mode === "interview" ? "staff_interview" : "staff_data_entry",
        verification_note: note || null,
      });
      toast.success("Answers saved"); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const doReview = async (action, payload = {}) => {
    setBusy(action);
    try {
      await api.post(`/organizers/${oid}/review`, { action, ...payload });
      toast.success(`${action.replace(/_/g, " ")} done`); setDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const doDna = async (q, enabled) => {
    setBusy("dna");
    try { await api.post(`/organizers/${oid}/does-not-apply`, { question_key: q.key, enabled }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitOrg = async () => {
    setBusy("submit");
    try { if (dirty) await saveAnswers(); await api.post(`/organizers/${oid}/submit`); toast.success("Organizer submitted"); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.unresolved_questions)) toast.error(`${d.unresolved_question_count} item(s) still require attention.`);
      else toast.error(formatApiErrorDetail(d));
      load();
    }
    finally { setBusy(""); }
  };

  const toggleOverride = async () => {
    try { await api.post(`/organizers/${oid}/override`, { enabled: !org.override_mode }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="flex justify-center py-24"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="staff-organizer-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><Icons.ArrowLeft className="h-4 w-4" />Back</button>

      {/* header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">Organizer</p>
            <h1 className="text-xl font-bold text-slate-900">{org.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {client?.full_name} {client?.client_number ? `· ${client.client_number}` : ""} {data.engagement?.engagement_number ? `· ${data.engagement.engagement_number}` : ""}
            </p>
          </div>
          <span data-testid="org-status" className={`text-xs px-2.5 py-1 rounded-full ${STATUS_TONE[org.status] || "bg-slate-100"}`}>{(org.status || "").replace(/_/g, " ")}</span>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${state.progress}%` }} /></div>
          <span className="text-sm font-semibold text-slate-700" data-testid="org-progress">{state.progress}%</span>
        </div>
        <p className="text-xs text-slate-400 mt-1.5">{state.required_remaining} required question(s) remaining · v{state.version} · completion: {(org.completion_method || "—").replace(/_/g, " ")}</p>
      </div>

      {/* mode controls */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Entry mode</span>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          {[["interview", "Interview", "Headphones"], ["data_entry", "Data Entry", "Keyboard"]].map(([k, lbl, ic]) => {
            const Ic = Icons[ic];
            return <button key={k} onClick={() => setMode(k)} data-testid={`org-mode-${k}`}
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 ${mode === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <Ic className="h-3.5 w-3.5" />{lbl}</button>;
          })}
        </div>
        <button onClick={toggleOverride} data-testid="org-override-toggle"
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border ${org.override_mode ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
          <Icons.UserCog className="h-3.5 w-3.5" />Override {org.override_mode ? "On" : "Off"}
        </button>
        <p className="text-xs text-slate-400 ml-auto">Answers you enter are tagged with your name & method (provenance).</p>
      </div>

      {/* Allison AI Review (post-submission) */}
      <AllisonReviewPanel oid={oid} clientId={org.client_id} navigate={navigate} />

      {/* DRL-2A: Allison Preparation Banner (pre-send proposals) */}
      {aiProposalCount > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="ai-preparation-banner">
          <div className="flex items-start gap-3">
            <Icons.Sparkles className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-indigo-900">Allison has prepared {aiProposalCount} suggestion{aiProposalCount !== 1 ? "s" : ""}</p>
              {aiMeta?.preparation_summary && (
                <p className="text-xs text-indigo-700 mt-0.5 leading-snug">{aiMeta.preparation_summary}</p>
              )}
              <p className="text-[11px] text-indigo-500 mt-1">
                Review each suggestion below — Accept to promote to a canonical answer, or Ignore to skip.
                Nothing is sent to the client until you click Send Organizer Request.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* sections */}
      <div className="space-y-4" data-testid="org-sections">
        {state.sections.map((sec) => (
          <div key={sec.key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
              <h2 className="font-semibold text-slate-800 text-sm">{sec.label}{sec.waived && <span className="ml-2 text-[11px] text-slate-400">(section waived)</span>}</h2>
              {!sec.waived && (
                <button onClick={() => doReview("waive_section", { section_key: sec.key })} data-testid={`org-waive-section-${sec.key}`}
                  className="text-[11px] text-amber-600 hover:underline">Waive section</button>
              )}
            </div>
            {sec.questions.length === 0 ? (
              <div className="px-5 py-4 text-xs text-slate-400">No applicable questions based on current answers.</div>
            ) : (
              <div className="px-3 py-1">
                {sec.questions.map((q) => {
                  const proposal = aiProposals[q.key];
                  const hasProposal = proposal && !proposal.dismissed && q.answer === null && q.answer === undefined;
                  // Show proposal chip for questions that are unanswered and have a proposal
                  const showProposal = proposal && !proposal.dismissed && (q.answer === null || q.answer === undefined || q.answer === "");
                  return (
                    <div key={q.key} className="group relative">
                      <QuestionRow q={q} value={getVal(q)} busy={!!busy} onChange={(v) => setDraft((d) => ({ ...d, [q.key]: v }))} onDna={(en) => doDna(q, en)} />
                      {!q.waived && q.required && (
                        <button onClick={() => doReview("waive_question", { question_key: q.key })}
                          className="absolute top-3 right-1 opacity-0 group-hover:opacity-100 text-[10px] text-amber-600 hover:underline" data-testid={`org-waive-q-${q.key}`}>waive</button>
                      )}
                      {showProposal && (
                        <div className="mx-2 mb-2 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2.5"
                          data-testid={`ai-proposal-${q.key}`}>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide">
                                {proposal.classification === "confirm" ? "Allison suggests (confirm)" : "Allison suggests"}
                                {proposal.confidence != null && (
                                  <span className="ml-2 font-normal text-indigo-400">{proposal.confidence}% confidence</span>
                                )}
                              </p>
                              <p className="text-sm text-indigo-900 font-medium mt-0.5" data-testid={`ai-proposed-value-${q.key}`}>
                                {proposal.proposed_value === true ? "Yes" : proposal.proposed_value === false ? "No" : String(proposal.proposed_value ?? "—")}
                              </p>
                              {proposal.proposed_note && (
                                <p className="text-[11px] text-indigo-600 mt-0.5 leading-snug" data-testid={`ai-proposal-note-${q.key}`}>{proposal.proposed_note}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                onClick={() => acceptAiAnswer(q.key)}
                                disabled={busy === `ai-accept-${q.key}`}
                                data-testid={`ai-accept-${q.key}`}
                                className="inline-flex items-center gap-1 text-[11px] font-medium rounded-md bg-indigo-600 text-white px-2.5 py-1 hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                                {busy === `ai-accept-${q.key}` ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Check className="h-3 w-3" />}
                                Accept
                              </button>
                              <button
                                onClick={() => dismissAiAnswer(q.key)}
                                disabled={busy === `ai-dismiss-${q.key}`}
                                data-testid={`ai-dismiss-${q.key}`}
                                className="inline-flex items-center gap-1 text-[11px] font-medium rounded-md border border-slate-200 text-slate-500 px-2.5 py-1 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                                Ignore
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* internal notes */}
      {org.review_notes?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Internal Notes</h3>
          <div className="space-y-1.5">
            {org.review_notes.map((n, i) => <div key={i} className="text-xs text-slate-600"><span className="font-medium">{n.by}</span>: {n.note}</div>)}
          </div>
        </div>
      )}

      {/* sticky action bar */}
      <div className="sticky bottom-4 bg-white rounded-xl border border-slate-200 shadow-lg p-3 flex flex-wrap items-center gap-2" data-testid="org-action-bar">
        <button onClick={saveAnswers} disabled={!dirty || busy === "save"} data-testid="org-save-btn"
          className="inline-flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2">
          {busy === "save" ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />}Save answers{dirty ? ` (${Object.keys(draft).length})` : ""}
        </button>
        <button onClick={submitOrg} disabled={state.required_remaining > 0 || busy === "submit"} data-testid="org-submit-btn"
          title={state.required_remaining > 0 ? "Answer all required questions first" : ""}
          className="inline-flex items-center gap-1.5 text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2">
          <Icons.Send className="h-4 w-4" />Submit
        </button>
        <div className="h-6 w-px bg-slate-200 mx-1" />
        <button onClick={() => doReview("approve")} disabled={busy === "approve"} data-testid="org-approve-btn"
          className="inline-flex items-center gap-1.5 text-sm border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-2"><Icons.CheckCircle2 className="h-4 w-4" />Approve & Complete</button>
        <button onClick={() => setDialog({ action: "request_clarification" })} data-testid="org-clarify-btn"
          className="inline-flex items-center gap-1.5 text-sm border border-amber-200 text-amber-700 hover:bg-amber-50 rounded-lg px-3 py-2"><Icons.MessageCircleQuestion className="h-4 w-4" />Request Clarification</button>
        <button onClick={() => doReview("reopen")} data-testid="org-reopen-btn"
          className="inline-flex items-center gap-1.5 text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-2"><Icons.RotateCcw className="h-4 w-4" />Reopen</button>
        <button onClick={() => setDialog({ action: "add_note" })} data-testid="org-note-btn"
          className="inline-flex items-center gap-1.5 text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-2"><Icons.StickyNote className="h-4 w-4" />Add Note</button>
      </div>

      {dialog && <NoteDialog dialog={dialog} onClose={() => setDialog(null)}
        onConfirm={(text) => doReview(dialog.action, { note: text })} />}
    </div>
  );
}

const CAT_META = {
  missing_information: { label: "Missing Information", icon: "HelpCircle" },
  inconsistency: { label: "Inconsistencies", icon: "GitCompareArrows" },
  missing_document: { label: "Missing Documents", icon: "FileWarning" },
  compliance_risk: { label: "Compliance Risks", icon: "ShieldAlert" },
  missing_election: { label: "Missing Elections", icon: "Vote" },
  planning_opportunity: { label: "Planning Opportunities", icon: "Lightbulb" },
  internal_review_note: { label: "Internal Review Notes", icon: "StickyNote" },
};
const SEV_META = {
  critical: "bg-rose-100 text-rose-700 border-rose-200", high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-slate-100 text-slate-600 border-slate-200",
  informational: "bg-blue-100 text-blue-700 border-blue-200",
};
const STATUS_META = {
  open: "bg-slate-100 text-slate-500", accepted: "bg-emerald-100 text-emerald-700",
  dismissed: "bg-slate-200 text-slate-500 line-through", resolved: "bg-emerald-600 text-white",
};

function AllisonReviewPanel({ oid, clientId, navigate }) {
  const [rev, setRev] = useState(undefined);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState({});
  const [contra, setContra] = useState(null); // { finding }

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/organizer-reviews/organizer/${oid}`); setRev(data); }
    catch { setRev({ exists: false }); }
  }, [oid]);
  useEffect(() => { load(); }, [load]);
  const review = rev?.review;
  const status = review?.review_status;
  useEffect(() => {
    if (status !== "running") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [status, load]);

  const scrollToQuestion = (key) => {
    const el = document.querySelector(`[data-testid='org-question-${key}']`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const disposition = async (fid, action, payload) => {
    setBusy(fid + action);
    try { await api.post(`/organizer-reviews/${review.id}/findings/${fid}/${action}`, payload || {}); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const routeContradiction = async (finding, value, reason) => {
    setBusy(finding.id + "contra");
    try {
      const { data } = await api.post(`/organizer-reviews/${review.id}/findings/${finding.id}/resolve-contradiction`, { value, reason });
      toast.success(data.duplicate ? "A pending proposal already exists — opening Proposed Changes." : "Proposed change created — review & approve it in the client's Tax Profile.");
      setContra(null);
      await load();
      openProposedChanges(data.client_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const openProposedChanges = (cid) => {
    try { localStorage.setItem(`tf.ws.lastTab.${cid}`, "tax"); } catch { /* ignore */ }
    navigate(`/client-workspace/${cid}`);
  };
  const rerun = async () => {
    setBusy("rerun");
    try { await api.post(`/organizer-reviews/version/${review.organizer_version_id}/rerun`); toast.success("Allison is re-reviewing…"); setTimeout(load, 1500); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (rev === undefined || !rev.exists) return null;
  const findings = review.findings || [];
  const sc = review.severity_counts || {};
  const byCat = {};
  findings.forEach((f) => { (byCat[f.category] = byCat[f.category] || []).push(f); });

  return (
    <div className="bg-gradient-to-br from-violet-50 to-white rounded-xl border border-violet-200 shadow-sm overflow-hidden" data-testid="allison-review-panel">
      <div className="px-5 py-3.5 border-b border-violet-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-violet-600 flex items-center justify-center"><Icons.Brain className="h-4 w-4 text-white" /></div>
          <div>
            <h2 className="font-semibold text-violet-900 text-sm">Allison — Organizer Review</h2>
            <p className="text-[11px] text-violet-500">Read-only advisory · v{review.review_version} · {review.llm_model}</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="allison-review-status">
          {status === "running" && <span className="inline-flex items-center gap-1.5 text-xs text-violet-700"><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Reviewing…</span>}
          {status === "failed" && <span className="text-xs text-rose-600">Review failed</span>}
          {status === "completed" && <span className="text-xs text-violet-700 font-medium">{findings.length} finding(s)</span>}
          {(status === "completed" || status === "failed") && (
            <button onClick={rerun} disabled={busy === "rerun"} data-testid="allison-rerun-btn"
              className="inline-flex items-center gap-1 text-xs border border-violet-200 text-violet-700 hover:bg-violet-50 rounded-lg px-2.5 py-1">
              <Icons.RefreshCw className="h-3.5 w-3.5" />{status === "failed" ? "Retry" : "Re-run"}
            </button>
          )}
        </div>
      </div>

      {status === "running" && <div className="px-5 py-6 text-sm text-violet-600">Allison is reviewing this submission. Findings will appear here shortly.</div>}
      {status === "failed" && <div className="px-5 py-5 text-sm text-slate-600">{review.error || "Allison couldn't complete the review."} A retry is available — this does not block production.</div>}

      {status === "completed" && (
        <div className="p-4 space-y-3">
          {review.summary && <p className="text-sm text-slate-700 bg-white/70 rounded-lg p-3 border border-violet-100" data-testid="allison-summary">{review.summary}</p>}
          <KnowledgeRetrievalPanel research={review.research_package} summary={review.knowledge_retrieval_summary} testidPrefix="org" />
          <HistoricalContextPanel context={review.case_context_package} testidPrefix="org" />
          <OrganizerGovernance review={review} />
          <div className="flex flex-wrap gap-1.5" data-testid="allison-severity-counts">
            {["critical", "high", "medium", "low", "informational"].map((s) => (sc[s] > 0) && (
              <span key={s} className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${SEV_META[s]}`}>{sc[s]} {s}</span>
            ))}
            {findings.length === 0 && <span className="text-sm text-emerald-700">No issues found — clean intake.</span>}
          </div>
          {Object.keys(CAT_META).filter((c) => byCat[c]?.length).map((cat) => {
            const Ic = Icons[CAT_META[cat].icon] || Icons.Dot;
            return (
              <div key={cat} className="bg-white rounded-lg border border-slate-200" data-testid={`allison-cat-${cat}`}>
                <div className="px-3.5 py-2 border-b border-slate-100 flex items-center gap-2">
                  <Ic className="h-4 w-4 text-violet-500" /><span className="text-xs font-semibold text-slate-700">{CAT_META[cat].label}</span>
                  <span className="text-[11px] text-slate-400">({byCat[cat].length})</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {byCat[cat].map((f) => (
                    <div key={f.id} className="px-3.5 py-3" data-testid={`allison-finding-${f.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 border ${SEV_META[f.severity]}`}>{f.severity}</span>
                            {f.scope_class && f.scope_class !== "required" && (
                              <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 border ${f.scope_class === "out_of_scope" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid={`allison-finding-scope-${f.id}`}>
                                {f.scope_class === "out_of_scope" ? "out of scope" : "recommended"}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400">{f.confidence}% confidence</span>
                            <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 ${STATUS_META[f.status]}`} data-testid={`allison-finding-status-${f.id}`}>{f.status}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-800 mt-1">{f.summary}</p>
                          <p className="text-xs text-slate-600 mt-0.5">{f.details}</p>
                        </div>
                        <button onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))} data-testid={`allison-finding-toggle-${f.id}`} className="text-slate-400 hover:text-slate-600 shrink-0">
                          <Icons.ChevronDown className={`h-4 w-4 transition-transform ${open[f.id] ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                      {open[f.id] && (
                        <div className="mt-2.5 space-y-2 text-xs">
                          {f.reasoning && <p className="text-slate-600"><span className="font-semibold text-slate-500">Why: </span>{f.reasoning}</p>}
                          {f.recommended_action && <p className="text-slate-600"><span className="font-semibold text-slate-500">Recommended: </span>{f.recommended_action}</p>}
                          {f.authority && <p className="text-violet-700 bg-violet-50 rounded px-2 py-1" data-testid={`allison-authority-${f.id}`}><Icons.BookOpen className="h-3 w-3 inline mr-1" />{f.authority}</p>}
                          {f.contradiction && (f.contradiction.kind || (f.contradiction.competing_values || []).length > 0) && (
                            <div className={`rounded-lg px-2.5 py-2 border ${f.contradiction.kind === "timeline_change" ? "bg-sky-50 border-sky-200" : "bg-rose-50 border-rose-200"}`} data-testid={`allison-contradiction-${f.id}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                {f.contradiction.kind === "timeline_change"
                                  ? <><Icons.Clock className="h-3 w-3 text-sky-600" /><span className="text-[10px] font-semibold uppercase tracking-wide text-sky-700">Timeline change</span></>
                                  : <><Icons.GitCompareArrows className="h-3 w-3 text-rose-600" /><span className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Contradiction with history</span></>}
                                {f.contradiction.field_label && <span className="text-[10px] text-slate-500">· {f.contradiction.field_label}</span>}
                              </div>
                              {f.contradiction.explanation && <p className="text-slate-600 mb-1">{f.contradiction.explanation}</p>}
                              {(f.contradiction.competing_values || []).length > 0 && (
                                <ul className="space-y-0.5">
                                  {f.contradiction.competing_values.map((v, i) => (
                                    <li key={i} className="text-slate-700"><span className="font-medium">{v.value}</span>{v.source && <span className="text-slate-400"> — {v.source}</span>}{v.date && <span className="text-slate-400"> ({v.date})</span>}</li>
                                  ))}
                                </ul>
                              )}
                              {!f.contradiction.resolvable && f.contradiction.kind === "true_contradiction" && (
                                <p className="text-[10px] text-slate-400 mt-1 italic">No governed client-record field — read-only. Reconcile with the client directly.</p>
                              )}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {(f.related_questions || []).map((qk) => (
                              <button key={qk} onClick={() => scrollToQuestion(qk)} data-testid={`allison-jump-q-${f.id}`}
                                className="text-[11px] border border-slate-200 rounded px-2 py-0.5 text-slate-600 hover:border-violet-300"><Icons.CornerDownRight className="h-3 w-3 inline mr-0.5" />{qk}</button>
                            ))}
                            {(f.related_documents || []).length > 0 && (
                              <button onClick={() => navigate("/documents")} data-testid={`allison-jump-doc-${f.id}`}
                                className="text-[11px] border border-slate-200 rounded px-2 py-0.5 text-slate-600 hover:border-violet-300"><Icons.FileText className="h-3 w-3 inline mr-0.5" />Documents</button>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                        {f.contradiction?.resolvable && (
                          f.record_update_proposal_id ? (
                            <button onClick={() => openProposedChanges(review.client_id)} data-testid={`allison-view-proposal-${f.id}`}
                              className="inline-flex items-center gap-1 text-[11px] border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg px-2.5 py-1"><Icons.GitPullRequestArrow className="h-3 w-3" />View Proposed Change</button>
                          ) : (
                            <button onClick={() => setContra({ finding: f })} disabled={busy === f.id + "contra"} data-testid={`allison-resolve-contradiction-${f.id}`}
                              className="inline-flex items-center gap-1 text-[11px] border border-rose-200 text-rose-700 hover:bg-rose-50 rounded-lg px-2.5 py-1"><Icons.GitCompareArrows className="h-3 w-3" />Resolve Contradiction</button>
                          )
                        )}
                        <button onClick={() => disposition(f.id, "accept")} disabled={busy === f.id + "accept"} data-testid={`allison-accept-${f.id}`}
                          className="text-[11px] border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg px-2.5 py-1">Accept</button>
                        <button onClick={() => disposition(f.id, "resolve", { note: "" })} disabled={busy === f.id + "resolve"} data-testid={`allison-resolve-${f.id}`}
                          className="text-[11px] border border-violet-200 text-violet-700 hover:bg-violet-50 rounded-lg px-2.5 py-1">Resolve</button>
                        <button onClick={() => disposition(f.id, "dismiss", { reason: "" })} disabled={busy === f.id + "dismiss"} data-testid={`allison-dismiss-${f.id}`}
                          className="text-[11px] border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-lg px-2.5 py-1">Dismiss</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {contra && <ResolveContradictionDialog finding={contra.finding} busy={busy === contra.finding.id + "contra"}
        onClose={() => setContra(null)} onConfirm={(value, reason) => routeContradiction(contra.finding, value, reason)} />}
    </div>
  );
}

function ResolveContradictionDialog({ finding, busy, onClose, onConfirm }) {
  const cd = finding.contradiction || {};
  const values = cd.competing_values || [];
  const [sel, setSel] = useState(values.length === 1 ? values[0].value : "");
  const [custom, setCustom] = useState("");
  const [reason, setReason] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const chosen = useCustom ? custom.trim() : sel;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="org-contradiction-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.GitCompareArrows className="h-4 w-4 text-rose-600" />Resolve Contradiction — {cd.record_field_label || cd.field_label}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">This does not change any client record. It creates a <span className="font-semibold">pending proposed change</span> that you must Accept, Edit, or Reject in the client's Tax Profile → Proposed Changes.</p>
          {cd.explanation && <p className="text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 border border-slate-100">{cd.explanation}</p>}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Select the value to propose</div>
            <div className="space-y-1.5">
              {values.map((v, i) => (
                <label key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${!useCustom && sel === v.value ? "border-rose-300 bg-rose-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`org-contradiction-value-${i}`}>
                  <input type="radio" name="contra-val" checked={!useCustom && sel === v.value} onChange={() => { setUseCustom(false); setSel(v.value); }} className="mt-0.5" />
                  <span className="flex-1"><span className="font-medium text-slate-800">{v.value}</span>{v.source && <span className="block text-[11px] text-slate-400">{v.source}{v.date ? ` · ${v.date}` : ""}</span>}</span>
                </label>
              ))}
              <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${useCustom ? "border-rose-300 bg-rose-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="contra-val" checked={useCustom} onChange={() => setUseCustom(true)} />
                <input value={custom} onFocus={() => setUseCustom(true)} onChange={(e) => setCustom(e.target.value)} data-testid="org-contradiction-custom"
                  placeholder="Enter a different value…" className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm" />
              </label>
            </div>
          </div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="org-contradiction-reason"
            placeholder="Reason / note for the proposed change (optional)…" className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => { if (!chosen) return toast.error("Choose or enter a value first."); onConfirm(chosen, reason.trim() || undefined); }}
            disabled={busy || !chosen} data-testid="org-contradiction-submit"
            className="text-sm px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
            {busy ? "Creating…" : "Create Proposed Change & Review"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ dialog, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const titles = { request_clarification: "Request Clarification", add_note: "Add Internal Note" };
  const required = dialog.action === "request_clarification";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="org-note-dialog">
        <DialogHeader><DialogTitle>{titles[dialog.action]}</DialogTitle></DialogHeader>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="org-note-text"
          placeholder={required ? "What do you need the client to clarify? (sent to their portal)" : "Internal note (staff only)…"}
          className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => { if (required && !text.trim()) return toast.error("A note is required."); onConfirm(text); }}
            data-testid="org-note-confirm" className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">Confirm</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
